import {
    Button,
    DatePicker,
    Form, Input, notification, Select, Space, Spin,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useEffect, useRef, useState } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import moment from 'moment';
import { InputMask } from '@react-input/mask';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { IUseModalEdit } from '../../hooks/table/useModalEdit';
import { useCallForm } from '../../hooks/useCallForm';
import { useApiFetch } from '../../hooks/api/useApiFetch';
import { ICallFormLayout } from '../../types/layouts/call-form';
import { tableStateSelector } from '../../redux/selectors/tables';

interface ICallFormProps {
    modal: IUseModalEdit;
    layout: ICallFormLayout;
}

const CallForm = ({
    layout, modal,
}: ICallFormProps) => {
    const {
        objectId, setHideCloseButton, hide, show, updateTableData,
    } = modal;

    const pressedButton = useRef<'exit'|'next'>();

    const api = useApiFetch();
    const {
        amount, clear, formData, phone, statuses,
    } = useCallForm({ withPhone: pressedButton.current === 'next', id: objectId });
    const [form] = useForm();
    const [callForm] = useForm();
    const [formReady, setFormReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showDeposit, setShowDeposit] = useState(false);
    const [time, setTime] = useState<string>();
    const [searchParams] = useSearchParams();
    const tableState = useSelector(tableStateSelector(layout.tableFilterSelector));

    const search = searchParams.get('search') || undefined;

    useEffect(() => {
        form.setFieldsValue(formData.values);
    }, [formData.values]);

    useEffect(() => {
        setHideCloseButton(typeof phone.value === 'string');
    }, [phone.value]);

    const sendCallForm = async () => {
        try {
            await callForm.validateFields();
            setLoading(true);
            const response = await api.fetchData<{ id: number } | null>(`/calls/form/${objectId}/save`, 'post', true, {
                commentary: callForm.getFieldValue('commentary'),
                date: callForm.getFieldValue('date') ? moment(callForm.getFieldValue('date')).format('YYYY-MM-DD') : undefined,
                time: callForm.getFieldValue('time'),
                status: callForm.getFieldValue('status'),
                filters: tableState?.filters,
                sorters: tableState?.sorters,
                search,
            });
            await updateTableData();
            setLoading(false);

            if (!response.success) {
                notification.error({
                    message: response.message,
                });
            }

            return response;
        } catch (err) {
            return undefined;
        }
    };

    const onExitClick = async () => {
        pressedButton.current = 'exit';
        const response = await sendCallForm();
        if (response?.success) {
            hide();
        }
    };

    const onNextClick = async () => {
        pressedButton.current = 'next';
        const response = await sendCallForm();
        if (response?.success && response?.data instanceof Object) {
            setFormReady(false);
            callForm.resetFields();
            form.resetFields();
            clear();
            show(response.data.id);
        }
    };

    const onValuesChange = (changedValues: any) => {
        if (changedValues instanceof Object) {
            if (typeof changedValues.time === 'string') {
                setTime(changedValues.time);
            }
        }
    };

    return (
        <Spin spinning={formData.loading}>
            <Form form={form} layout="vertical">
                <Form.Item label="Адрес заказа" name="address">
                    <Input readOnly />
                </Form.Item>
                <Form.Item label="Изделие" name="product.name">
                    <Input readOnly />
                </Form.Item>
                <Form.Item label="Мастер" name="master.fio">
                    <Input readOnly />
                </Form.Item>
                <Form.Item label="Дата встречи" name="meeting_date">
                    <Input readOnly />
                </Form.Item>
                <Form.Item label="Дата закрытия" name="closed_at">
                    <Input readOnly />
                </Form.Item>
                <Form.Item label="ФИО клиента" name="client_fio">
                    <Input readOnly />
                </Form.Item>
                <Form.Item label="Телефон клиента">
                    <Space>
                        <Input readOnly value={phone.value} />
                        <Button
                            type="primary"
                            onClick={phone.get}
                            loading={phone.loading}
                        >
                            Показать
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
            <Form form={callForm} hidden={typeof phone.value === 'undefined'} layout="vertical" onValuesChange={onValuesChange}>
                <Form.Item label="Комментарий прозвона" name="commentary" rules={[{ required: true }]}>
                    <Input.TextArea />
                </Form.Item>
                <Form.Item label="Дата повторного прозвона" name="date">
                    <DatePicker className="w-100" format="DD.MM.YYYY" />
                </Form.Item>
                <Form.Item label="Время повторного прозвона" name="time">
                    <InputMask
                        className="ant-input w-100"
                        mask="ab:cd"
                        replacement={{
                            a: /[0-2]/,
                            b: typeof time === 'string' && time[0] === '2' ? /[0-3]/ : /[0-9]/,
                            c: /[0-5]/,
                            d: /[0-9]/,
                        }}
                        separate
                        placeholder="00:00"
                    />
                </Form.Item>
                <Form.Item label="Задаток">
                    <Space>
                        <Input readOnly value={showDeposit ? formData.values?.deposit || '-' : ''} />
                        <Button type="primary" onClick={() => setShowDeposit(true)}>
                            Показать
                        </Button>
                    </Space>
                </Form.Item>
                <Form.Item label="Сумма">
                    <Space>
                        <Input readOnly value={amount.value} />
                        <Button
                            type="primary"
                            onClick={amount.get}
                            loading={amount.loading}
                        >
                            Показать
                        </Button>
                    </Space>
                </Form.Item>
                <Form.Item label="Источник">
                    <Input readOnly value={form.getFieldValue('source.name')} />
                </Form.Item>
                <Form.Item label="Статус прозвона" name="status" rules={[{ required: true }]}>
                    <Select
                        options={statuses.values}
                        loading={statuses.values.length === 0}
                        onSelect={() => setFormReady(true)}
                    />
                </Form.Item>
                <Form.Item hidden={!formReady}>
                    <Space>
                        <Button
                            danger
                            disabled={loading && pressedButton.current === 'next'}
                            type="primary"
                            onClick={onExitClick}
                            loading={loading && pressedButton.current === 'exit'}
                        >
                            Выйти
                        </Button>
                        <Button
                            disabled={loading && pressedButton.current === 'exit'}
                            type="primary"
                            onClick={onNextClick}
                            loading={loading && pressedButton.current === 'next'}
                        >
                            Следующий
                            <ArrowRightOutlined />
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Spin>
    );
};

export default CallForm;
